import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios'
import Swal from 'sweetalert2'

import { useAuthStore } from '@/store/auth.store'
import { config } from '@/core/config'

// Extend Axios config types to include tempToken
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  tempToken?: string
}

interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  tempToken?: string
}

// Base API configuration - using centralized config from config.ts
// This ensures single source of truth: .env → config.ts → client.ts
const API_BASE_URL = config.api.baseUrl
const API_TIMEOUT = config.api.timeout

// Warn if API URL is not configured (check via config)
if (!config.api.baseUrl || config.api.baseUrl.includes('erpapi2026.erpaga.com')) {
  console.warn('⚠️ VITE_API_URL is not set in .env file. Using default URL.')
  console.warn('⚠️ Please create a .env file with VITE_API_URL=your_api_url')
}

/**
 * Axios client instance with interceptors for JWT handling and error management
 * Updated to handle both stored tokens and temporary tokens for specific requests
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Request interceptor: Add authorization header if token exists
apiClient.interceptors.request.use(
  (config: CustomInternalAxiosRequestConfig) => {
    // Check if a specific token is provided in the config
    const tempToken = config.tempToken
    
    if (tempToken) {
      config.headers.Authorization = `Bearer ${tempToken}`
      // Remove tempToken from config to avoid sending it in the request
      delete config.tempToken
    } else {
      // Use token from store if available
      const { token, isTokenExpired } = useAuthStore.getState()
      
      if (token && !isTokenExpired()) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // If data is FormData, remove Content-Type header to let Axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    // Log requests in development
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        headers: {
          ...config.headers,
          Authorization: config.headers.Authorization ? '[HIDDEN]' : undefined,
        },
        data: config.data instanceof FormData ? '[FormData]' : config.data,
      })
    }

    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor: Handle responses and errors globally
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses in development
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      })
    }

    return response
  },
  async (error: AxiosError) => {
    const { response, config } = error
    const { logout, isTokenExpired } = useAuthStore.getState()

    // Log errors in development
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.error(`❌ API Error: ${config?.method?.toUpperCase()} ${config?.url}`, {
        status: response?.status,
        data: response?.data,
        message: error.message,
      })
    }

    // Handle different error scenarios
    switch (response?.status) {
      case 401: {
        // Check if this was a request with a temporary token
        const wasUsingTempToken = (config as CustomInternalAxiosRequestConfig)?.tempToken
        
        if (wasUsingTempToken) {
          // If using temp token and got 401, it means the temp token is invalid
          console.error('❌ Temporary token invalid or expired')
        } else {
          // Regular 401 handling for stored tokens
          if (isTokenExpired()) {
            logout()
            Swal.fire({
              icon: 'error',
              title: 'Session Expired',
              text: 'Please login again.',
              confirmButtonText: 'OK',
              allowOutsideClick: false,
            }).then(() => {
              // Redirect to login page
              if (window.location.pathname !== '/login') {
                window.location.href = '/login'
              }
            })
          }
        }
        break
      }
      
      case 403:
        Swal.fire({
          icon: 'error',
          title: 'Access Denied',
          text: 'You do not have permission for this action.',
          confirmButtonText: 'OK',
        })
        break
        
      case 404:
        // Do not show global alert for 404 - let the caller handle it (e.g. list pages show empty state without toast/alert)
        break

      case 422:
        // Validation errors - handled by individual components
        break
        
      case 500:
        Swal.fire({
          icon: 'error',
          title: 'Server Error',
          text: 'Internal server error. Please try again later.',
          confirmButtonText: 'OK',
        })
        break
        
      default:
        if (error.code === 'ECONNABORTED') {
          Swal.fire({
            icon: 'error',
            title: 'Timeout',
            text: 'Request timeout. Please check your connection.',
            confirmButtonText: 'OK',
          })
        } else if (error.code === 'ERR_NETWORK') {
          Swal.fire({
            icon: 'error',
            title: 'Network Error',
            text: 'Network error. Please check your connection.',
            confirmButtonText: 'OK',
          })
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'An unexpected error occurred.',
            confirmButtonText: 'OK',
          })
        }
    }

    return Promise.reject(error)
  }
)

/**
 * Helper function to make API calls with a temporary token
 * Useful for calls that need a token before it's stored in the auth store
 */
export const apiClientWithToken = (token: string) => {
  return {
    get: (url: string, config: CustomAxiosRequestConfig = {}) => 
      apiClient.get(url, { ...config, tempToken: token } as CustomAxiosRequestConfig),
    post: (url: string, data?: any, config: CustomAxiosRequestConfig = {}) => 
      apiClient.post(url, data, { ...config, tempToken: token } as CustomAxiosRequestConfig),
    put: (url: string, data?: any, config: CustomAxiosRequestConfig = {}) => 
      apiClient.put(url, data, { ...config, tempToken: token } as CustomAxiosRequestConfig),
    delete: (url: string, config: CustomAxiosRequestConfig = {}) => 
      apiClient.delete(url, { ...config, tempToken: token } as CustomAxiosRequestConfig),
  }
}

export default apiClient 